import { ref, watch, nextTick, onUnmounted } from 'vue';

/**
 * Composable for trapping focus within a container.
 * Used for modals, drawers, and other overlay content to ensure
 * keyboard users can't Tab outside the container.
 */
export function useFocusTrap(containerSelector: string) {
  const isActive = ref(false);
  let previousActiveElement: HTMLElement | null = null;

  const getFocusableElements = (): HTMLElement[] => {
    const container = document.querySelector(containerSelector);
    if (!container) return [];

    return Array.from(
      container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null); // Only visible elements
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (!isActive.value) return;
    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0]!;
    const lastElement = focusableElements[focusableElements.length - 1]!;

    if (event.shiftKey) {
      // Shift + Tab: moving backwards
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  };

  const activate = () => {
    previousActiveElement = document.activeElement as HTMLElement;
    isActive.value = true;

    // Focus first focusable element after trap activates
    nextTick(() => {
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0]!.focus();
      }
    });
  };

  const deactivate = (returnFocus = true) => {
    isActive.value = false;
    if (returnFocus && previousActiveElement) {
      previousActiveElement.focus();
    }
    previousActiveElement = null;
  };

  // Watch for changes to isActive and manage event listener
  watch(isActive, (active) => {
    if (active) {
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('keydown', handleKeydown);
    }
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
  });

  return {
    isActive,
    activate,
    deactivate,
  };
}
